class RughausInfoMenu extends HTMLElement {
  #trigger;
  #panel;
  #hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
  #closeTimer;

  connectedCallback() {
    this.#trigger = this.querySelector('.rughaus-info-menu__trigger');
    this.#panel = this.querySelector('.rughaus-info-menu__panel');

    if (!(this.#trigger instanceof HTMLButtonElement) || !(this.#panel instanceof HTMLElement)) return;

    this.#trigger.addEventListener('pointerenter', this.#handlePointerEnter);
    this.#trigger.addEventListener('pointerleave', this.#handleTriggerLeave);
    this.#panel.addEventListener('pointerenter', this.#handlePointerEnter);
    this.#panel.addEventListener('pointerleave', this.#handlePanelLeave);
    this.addEventListener('focusin', this.#handleFocusIn);
    this.addEventListener('focusout', this.#handleFocusOut);
    this.#trigger.addEventListener('click', this.#handleClick);
    window.addEventListener('resize', this.#updatePanelPosition);
  }

  disconnectedCallback() {
    clearTimeout(this.#closeTimer);
    this.#trigger?.removeEventListener('pointerenter', this.#handlePointerEnter);
    this.#trigger?.removeEventListener('pointerleave', this.#handleTriggerLeave);
    this.#panel?.removeEventListener('pointerenter', this.#handlePointerEnter);
    this.#panel?.removeEventListener('pointerleave', this.#handlePanelLeave);
    this.removeEventListener('focusin', this.#handleFocusIn);
    this.removeEventListener('focusout', this.#handleFocusOut);
    this.#trigger?.removeEventListener('click', this.#handleClick);
    window.removeEventListener('resize', this.#updatePanelPosition);
  }

  #handlePointerEnter = () => {
    if (!this.#hoverQuery.matches) return;
    clearTimeout(this.#closeTimer);
    this.#setOpen(true);
  };

  #handleTriggerLeave = () => {
    if (!this.#hoverQuery.matches) return;
    clearTimeout(this.#closeTimer);
    this.#closeTimer = window.setTimeout(() => this.#setOpen(false), 220);
  };

  #handlePanelLeave = () => {
    if (!this.#hoverQuery.matches) return;
    clearTimeout(this.#closeTimer);
    this.#closeTimer = window.setTimeout(() => this.#setOpen(false), 80);
  };

  #handleFocusIn = () => {
    clearTimeout(this.#closeTimer);
    this.#setOpen(true);
  };

  #handleFocusOut = () => {
    requestAnimationFrame(() => {
      if (!this.contains(document.activeElement) && !this.matches(':hover')) {
        this.#setOpen(false);
      }
    });
  };

  #handleClick = () => {
    if (this.#hoverQuery.matches) {
      this.#setOpen(true);
      return;
    }

    this.#setOpen(!this.classList.contains('is-open'));
  };

  #setOpen(open) {
    if (!this.#trigger || !this.#panel) return;

    if (open) {
      document.querySelectorAll('rughaus-info-menu.is-open').forEach((menu) => {
        if (menu === this) return;

        menu.classList.remove('is-open');
        menu.querySelector('.rughaus-info-menu__trigger')?.setAttribute('aria-expanded', 'false');
        menu.querySelector('.rughaus-info-menu__panel')?.setAttribute('aria-hidden', 'true');
      });
    }

    this.classList.toggle('is-open', open);
    this.#trigger.setAttribute('aria-expanded', String(open));
    this.#panel.setAttribute('aria-hidden', String(!open));
    if (open) this.#updatePanelPosition();
  }

  #updatePanelPosition = () => {
    if (!this.classList.contains('is-open') && this.#trigger?.getAttribute('aria-expanded') !== 'true') return;

    const triggerRect = this.#trigger.getBoundingClientRect();
    const panelRect = this.#panel.getBoundingClientRect();
    const triggerLeft = Math.max(0, Math.round(triggerRect.left - panelRect.left) + 6);
    this.style.setProperty('--rughaus-info-link-left', `${triggerLeft}px`);
  };
}

if (!customElements.get('rughaus-info-menu')) {
  customElements.define('rughaus-info-menu', RughausInfoMenu);
}
